import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import { isAbortableAgentExecution } from './agent-executions-list.model';

type SelectionState = {
  selected: ReadonlyMap<string, AgentExecutionDto>;
  isAllFilteredSelected: boolean;
  excludedIds: ReadonlySet<string>;
};

const EMPTY_SELECTION: SelectionState = {
  selected: new Map(),
  isAllFilteredSelected: false,
  excludedIds: new Set(),
};

const toggleInSet = ({
  ids,
  id,
  isIncluded,
}: {
  ids: ReadonlySet<string>;
  id: string;
  isIncluded: boolean;
}): ReadonlySet<string> =>
  isIncluded
    ? new Set([...ids, id])
    : new Set([...ids].filter((existingId) => existingId !== id));

export function useAgentExecutionsSelection({
  resetKey,
  totalCount,
  fetchAll,
}: {
  resetKey: string;
  totalCount: number;
  fetchAll: () => Promise<AgentExecutionDto[]>;
}) {
  const [state, setState] = useState<SelectionState>(EMPTY_SELECTION);
  const previousResetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (previousResetKeyRef.current === resetKey) {
      return;
    }

    previousResetKeyRef.current = resetKey;
    setState(EMPTY_SELECTION);
  }, [resetKey]);

  const selectedCount = state.isAllFilteredSelected
    ? Math.max(totalCount - state.excludedIds.size, 0)
    : state.selected.size;
  const areAllSelected =
    state.isAllFilteredSelected &&
    state.excludedIds.size === 0 &&
    totalCount > 0;
  const abortableCount = useMemo(
    () =>
      state.isAllFilteredSelected
        ? null
        : [...state.selected.values()].filter(isAbortableAgentExecution).length,
    [state],
  );

  const isExecutionSelected = useCallback(
    (execution: AgentExecutionDto) =>
      state.isAllFilteredSelected
        ? !state.excludedIds.has(execution.id)
        : state.selected.has(execution.id),
    [state],
  );

  const toggleExecution = useCallback(
    ({
      execution,
      selected,
    }: {
      execution: AgentExecutionDto;
      selected: boolean;
    }) =>
      setState((current) => {
        if (current.isAllFilteredSelected) {
          return {
            ...current,
            excludedIds: toggleInSet({
              ids: current.excludedIds,
              id: execution.id,
              isIncluded: !selected,
            }),
          };
        }

        const next = new Map(current.selected);

        if (selected) {
          next.set(execution.id, execution);
        } else {
          next.delete(execution.id);
        }

        return { ...current, selected: next };
      }),
    [],
  );

  const clearSelection = useCallback(() => setState(EMPTY_SELECTION), []);

  const selectAll = useCallback((selected: boolean) => {
    setState(
      selected
        ? { ...EMPTY_SELECTION, isAllFilteredSelected: true }
        : EMPTY_SELECTION,
    );
  }, []);

  const getSelectedExecutions = useCallback(async (): Promise<
    AgentExecutionDto[]
  > => {
    if (!state.isAllFilteredSelected) {
      return [...state.selected.values()];
    }

    const executions = await fetchAll();

    return executions.filter(
      (execution) => !state.excludedIds.has(execution.id),
    );
  }, [fetchAll, state]);

  return {
    selectedCount,
    areAllSelected,
    abortableCount,
    isExecutionSelected,
    toggleExecution,
    selectAll,
    clearSelection,
    getSelectedExecutions,
  };
}
